/**
 * WorkWise API service (contract section 4). The generic deployment endpoints
 * (trigger, cancel, list, detail) stay in services/deployments.ts.
 */
import { api } from './api'
import type { DeploymentRequest } from '@/types'
import type {
  CostEstimate,
  CostEstimateRequest,
  CustomerNumberAvailability,
  ExistingNetwork,
  SharedServer,
  SharedServerRole,
  WorkwiseEnvironment,
  WorkwisePayload,
  WorkwiseSizePrices,
  VmNames,
  WorkwiseVersion,
} from '@/types/workwise'

export const workwiseApi = {
  /** Plain array, not paginated. `region` is the Azure location code. */
  getSharedServers: (params: { role: SharedServerRole; region: string; environment: WorkwiseEnvironment }) =>
    api.get<SharedServer[]>('/shared-servers/', { params }),

  /**
   * Revision 4 (R4-B): the active existing networks a SaaS-Hybrid app server can join, recorded in
   * Django admin. Plain array ordered by name, then VNet. `region` is the Azure location code.
   */
  getExistingNetworks: (params: { region: string; environment: WorkwiseEnvironment }) =>
    api.get<ExistingNetwork[]>('/workwise/existing-networks/', { params }),

  checkCustomerNumber: (number: string, environment: WorkwiseEnvironment) =>
    api.get<CustomerNumberAvailability>('/workwise/customer-numbers/availability/', {
      params: { number, environment },
    }),

  getVersions: () => api.get<WorkwiseVersion[]>('/workwise/versions/'),

  /**
   * Next free VM names per role (contract section 8, B3). app/db need a valid
   * customer number; the gateway series does not.
   */
  getVmNames: (environment: WorkwiseEnvironment, customerNumber?: string) =>
    api.get<VmNames>('/workwise/vm-names/', {
      params: { environment, ...(customerNumber ? { customer_number: customerNumber } : {}) },
    }),

  /** 201 with the DeploymentRequest body; 400 flat dict; 409 when correlationId was reused. */
  createDeployment: (payload: WorkwisePayload) =>
    api.post<DeploymentRequest>('/workwise/deployments/', payload),

  /**
   * Revision 7 (R7-B): VM compute per month of every admin T-shirt size (Azure retail, with and without
   * Hybrid Benefit) and its max users. `region` is the Azure location code.
   */
  getSizePrices: (region: string) =>
    api.get<WorkwiseSizePrices>('/workwise/size-prices/', { params: { region } }),

  /**
   * Revision 7 (R7-B): priced lines per server (compute, SQL licence, OS and data disks, public IP,
   * shared share) for a lenient subset of the deployment payload. 400 = flat dotted-path errors.
   */
  estimateCost: (body: CostEstimateRequest) =>
    api.post<CostEstimate>('/workwise/cost-estimate/', body),
}
